import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { LogOut, RefreshCw, User, Shield, ArrowLeftRight, TrendingUp, ChevronRight, Radio, Save, HeartHandshake } from "lucide-react";
import { api, auth, formatRank } from "@/lib/api";
import { useMe, useBootstrap } from "@/lib/useMe";
import { useFlags } from "@/lib/useFlags";
import { T } from "@/lib/testIds";
import TeamBadge from "@/components/app/TeamBadge";
import { toast } from "sonner";

function LeagueMultiPicker({ selected, onChange }) {
  const { data } = useQuery({
    queryKey: ["leagues-mine"],
    queryFn: async () => (await api.get("/leagues/mine")).data,
    staleTime: 60_000,
  });
  const classic = (data?.classic || []).filter((l) => l.id !== 314);
  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else if (selected.length < 3) {
      onChange([...selected, id]);
    } else {
      toast("Max 3 leagues — deselect one first.");
    }
  };
  if (!data) return <div className="text-xs text-totem-textFaint mono animate-pulse">Loading leagues…</div>;
  if (classic.length === 0) {
    return <div className="text-xs text-totem-textDim">No classic leagues linked yet.</div>;
  }
  return (
    <div className="space-y-1.5 max-h-52 overflow-y-auto border border-totem-borderSoft rounded-lg p-2">
      {classic.map((l) => {
        const on = selected.includes(l.id);
        return (
          <label
            key={l.id}
            data-testid={`profile-league-${l.id}`}
            className="flex items-center gap-2 cursor-pointer text-sm px-2 py-1.5 rounded hover:bg-totem-surfaceHigh"
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => toggle(l.id)}
              className="w-4 h-4 accent-[#00FF85]"
            />
            <span className="flex-1 truncate">{l.name}</span>
            {on && <span className="mono text-[9px] uppercase text-totem-green">picked</span>}
          </label>
        );
      })}
      <div className="text-[10px] mono uppercase text-totem-textFaint pt-1 pl-2">
        {selected.length}/3 selected
      </div>
    </div>
  );
}

export default function Profile() {
  const nav = useNavigate();
  const flags = useFlags();
  const qc = useQueryClient();
  const { data, refetch, isRefetching } = useMe();
  const { data: boot } = useBootstrap();
  const mgr = data?.manager;
  const user = data?.user;

  const [displayName, setDisplayName] = useState("");
  const [favTeam, setFavTeam] = useState("");
  const [email, setEmail] = useState("");
  const [digestOptIn, setDigestOptIn] = useState(false);
  const [digestLeagueIds, setDigestLeagueIds] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || "");
      const ft = user.favorite_team_id;
      setFavTeam(ft === null || ft === undefined ? "" : String(ft));
      setEmail(user.email || "");
      setDigestOptIn(user.digest_opt_in !== false);
      setDigestLeagueIds(user.digest_league_ids || []);
      setDirty(false);
    }
  }, [user]);

  const saveProfile = useMutation({
    mutationFn: async () => {
      const body = {
        display_name: displayName.trim() || null,
        favorite_team_id: favTeam === "" ? null : Number(favTeam),
        email: email.trim() || null,
        digest_opt_in: digestOptIn,
        digest_league_ids: digestLeagueIds,
      };
      return (await api.patch("/me/profile", body)).data;
    },
    onSuccess: () => {
      toast.success("Profile saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const sendPreview = useMutation({
    mutationFn: async () => (await api.post("/me/digest/preview")).data,
    onSuccess: (d) => toast.success(`Preview sent to ${d.to}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Preview failed"),
  });

  const teams = boot?.teams || [];
  const favTeamMeta = favTeam === "0" ? { name: "Neutral Fan" } : teams.find((t) => String(t.id) === favTeam);

  const logout = () => {
    auth.clear();
    toast("Signed out");
    nav("/login");
  };

  return (
    <div data-testid={T.profilePage} className="space-y-4 pt-1 animate-fade-up">
      <section className="totem-card p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="grid place-items-center w-14 h-14 rounded-full bg-totem-green text-totem-bg">
            <User size={26} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="display-heading text-xl truncate">
              {user?.display_name || `${mgr?.player_first_name ?? ""} ${mgr?.player_last_name ?? ""}`.trim() || "—"}
            </div>
            <div className="mono text-xs text-totem-textDim uppercase truncate">
              {mgr?.name || "—"}
            </div>
            {favTeamMeta && (
              <div className="mono text-[10px] uppercase text-totem-textDim mt-1 flex items-center gap-1.5">
                {favTeam === "0" ? (
                  <><HeartHandshake size={11} className="text-totem-green" /> Neutral Fan</>
                ) : (
                  <>
                    {favTeamMeta.code && <TeamBadge teamCode={favTeamMeta.code} teamShort={favTeamMeta.short_name} size={14} />}
                    {favTeamMeta.name}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="totem-card-inner p-3">
            <div className="label-caps mb-1">Manager ID</div>
            <div className="mono font-semibold">{mgr?.id}</div>
          </div>
          <div className="totem-card-inner p-3">
            <div className="label-caps mb-1">Current GW</div>
            <div className="mono font-semibold">{mgr?.current_event || "—"}</div>
          </div>
          <div className="totem-card-inner p-3">
            <div className="label-caps mb-1">Overall rank</div>
            <div className="mono font-semibold text-totem-green">{formatRank(mgr?.summary_overall_rank)}</div>
          </div>
          <div className="totem-card-inner p-3">
            <div className="label-caps mb-1">Overall points</div>
            <div className="mono font-semibold">{mgr?.summary_overall_points ?? 0}</div>
          </div>
        </div>
      </section>

      {/* Editable profile */}
      <section className="totem-card p-5">
        <div className="label-caps mb-3">Your profile</div>
        <div className="space-y-3">
          <div>
            <label htmlFor="dn" className="label-caps block mb-1.5">Display name</label>
            <input
              id="dn"
              data-testid={T.profileDisplayNameInput}
              value={displayName}
              placeholder={`${mgr?.player_first_name || "Your name"}`}
              onChange={(e) => { setDisplayName(e.target.value); setDirty(true); }}
              className="w-full bg-totem-surfaceElev border border-totem-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-totem-green/60"
            />
          </div>
          <div>
            <label htmlFor="ft" className="label-caps block mb-1.5">Favourite EPL team</label>
            <select
              id="ft"
              data-testid={T.profileFavTeamSelect}
              value={favTeam}
              onChange={(e) => { setFavTeam(e.target.value); setDirty(true); }}
              className="w-full appearance-none bg-totem-surfaceElev border border-totem-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-totem-green/60"
            >
              <option value="">— not set —</option>
              <option value="0">🤝 Neutral Fan</option>
              <optgroup label="Premier League clubs">
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <button
            onClick={() => saveProfile.mutate()}
            data-testid={T.profileSaveButton}
            disabled={!dirty || saveProfile.isPending}
            className="w-full flex items-center justify-center gap-2 bg-totem-green text-totem-bg font-semibold rounded-lg py-2.5 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Save size={14} />
            {saveProfile.isPending ? "Saving…" : dirty ? "Save profile" : "Saved"}
          </button>
        </div>
      </section>

      {/* Weekly Roast Digest opt-in */}
      <section className="totem-card p-5">
        <div className="label-caps mb-3">Weekly Roast Digest</div>
        <p className="text-xs text-totem-textDim leading-relaxed mb-3">
          Every Tuesday morning we&apos;ll email you last GW&apos;s trophies and calamities from your
          chosen mini-leagues (up to 3). No password stuff — just the good bits.
        </p>
        <label htmlFor="digest-email" className="label-caps block mb-1.5">Email</label>
        <input
          id="digest-email"
          type="email"
          data-testid="profile-email-input"
          value={email}
          placeholder="you@example.com"
          onChange={(e) => { setEmail(e.target.value); setDirty(true); }}
          className="w-full bg-totem-surfaceElev border border-totem-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-totem-green/60 mb-3"
        />
        <label className="flex items-center gap-2 cursor-pointer text-sm mb-3">
          <input
            type="checkbox"
            data-testid="profile-digest-optin"
            checked={digestOptIn}
            onChange={(e) => { setDigestOptIn(e.target.checked); setDirty(true); }}
            className="w-4 h-4 accent-[#00FF85]"
          />
          <span>Yes, email me the Roast Digest every Tuesday</span>
        </label>

        <div className="label-caps mb-2">Leagues to include (up to 3)</div>
        <LeagueMultiPicker
          selected={digestLeagueIds}
          onChange={(next) => { setDigestLeagueIds(next); setDirty(true); }}
        />

        <button
          onClick={() => sendPreview.mutate()}
          disabled={sendPreview.isPending || dirty || !email || !digestLeagueIds.length}
          data-testid="profile-preview-digest"
          title={dirty ? "Save your changes first" : ""}
          className="mt-4 w-full flex items-center justify-center gap-2 border border-totem-gold/40 text-totem-gold rounded-lg py-2.5 mono uppercase text-xs tracking-wider hover:bg-totem-gold/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          {sendPreview.isPending ? "Sending…" : "Send me a test digest now"}
        </button>
      </section>

      <section className="totem-card p-5">
        <div className="label-caps mb-3">Team tools</div>
        <div className="space-y-3">
          {flags.isVisible("live_rank_tracker") && (
            <Link
              to="/live"
              className="w-full flex items-center justify-between p-3 rounded-lg border border-totem-border hover:border-totem-red/60 transition"
            >
              <span className="flex items-center gap-3">
                <Radio size={16} className="text-totem-red" />
                <span>Live rank tracker</span>
              </span>
              <ChevronRight size={16} className="text-totem-textDim" />
            </Link>
          )}
          {flags.isVisible("player_compare") && (
            <Link
              to="/compare"
              className="w-full flex items-center justify-between p-3 rounded-lg border border-totem-border hover:border-totem-gold/60 transition"
            >
              <span className="flex items-center gap-3">
                <ArrowLeftRight size={16} className="text-totem-gold" />
                <span>Compare two players</span>
              </span>
              <ChevronRight size={16} className="text-totem-textDim" />
            </Link>
          )}
          {flags.isVisible("rank_gap") && (
            <Link
              to="/rank-gap"
              className="w-full flex items-center justify-between p-3 rounded-lg border border-totem-border hover:border-totem-green/60 transition"
            >
              <span className="flex items-center gap-3">
                <TrendingUp size={16} className="text-totem-green" />
                <span>Rank gap benchmarks</span>
              </span>
              <ChevronRight size={16} className="text-totem-textDim" />
            </Link>
          )}
        </div>
      </section>

      <section className="totem-card p-5">
        <div className="label-caps mb-3">Account</div>
        <div className="space-y-3">
          <button
            onClick={() => refetch()}
            className="w-full flex items-center justify-between p-3 rounded-lg border border-totem-border hover:border-totem-green/60 transition"
          >
            <span className="flex items-center gap-3">
              <RefreshCw size={16} className={isRefetching ? "animate-spin" : ""} />
              <span>Refresh FPL data</span>
            </span>
          </button>
          <button
            onClick={() => {
              auth.clear();
              nav("/login");
            }}
            className="w-full flex items-center justify-between p-3 rounded-lg border border-totem-border hover:border-totem-amber/60 transition"
          >
            <span className="flex items-center gap-3">
              <Shield size={16} />
              <span>Re-link a different manager ID</span>
            </span>
          </button>
        </div>
      </section>

      <section className="totem-card p-5">
        <div className="label-caps mb-3">Session</div>
        <div className="text-xs text-totem-textDim mono">
          Linked {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
          <br />
          Last login {user?.last_login_at ? new Date(user.last_login_at).toLocaleString() : "—"}
        </div>
      </section>

      <button
        data-testid={T.topLogoutButton}
        onClick={logout}
        className="w-full flex items-center justify-center gap-2 border border-totem-red/50 text-totem-red rounded-lg py-3.5 mono uppercase text-xs tracking-wider hover:bg-totem-red/10 transition"
      >
        <LogOut size={16} /> Sign out
      </button>

      <div className="text-[10px] text-totem-textFaint mono text-center pb-4">
        FPL Totem never stores your FPL password. All data is fetched from the public FPL API.
      </div>
    </div>
  );
}
